import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  MessageSquare,
  Bell,
  CalendarDays,
  ClipboardList,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useGlobalNotificationsWs } from '../hooks/notifications/useGlobalNotificationsWs';
import NotificationBell from './notifications/NotificationBell';
import ChatNotificationsPanel from './notifications/ChatNotificationsPanel';
import TaskNotificationsPanel from './notifications/TaskNotificationsPanel';
import ProfileTab from './profile/ProfileTab';
import { selectTotalUnread } from '../redux/slices/chatUnread';
import { selectUnreadCount as selectUnreadTaskCount } from '../redux/slices/notifications';

type LeafItem = { kind: 'leaf'; to: string; label: string; Icon: typeof MessageSquare };
type GroupItem = {
  kind: 'group';
  id: string;
  label: string;
  Icon: typeof MessageSquare;
  children: { to: string; label: string }[];
};
type NavItem = LeafItem | GroupItem;

const NAV_ITEMS: NavItem[] = [
  { kind: 'leaf', to: '/chat', label: 'Chat', Icon: MessageSquare },
  {
    kind: 'group',
    id: 'task-management',
    label: 'Task Management',
    Icon: ClipboardList,
    children: [
      { to: '/todo', label: 'Daily Planner' },
      { to: '/projects', label: 'Projects' },
      { to: '/tasks/list', label: 'Tasks' },
      { to: '/clients', label: 'Clients' },
      { to: '/tasks/reports', label: 'Reports' },
    ],
  },
  { kind: 'leaf', to: '/calendar', label: 'Calendar', Icon: CalendarDays },
];

export default function AppSidebar() {
  const [collapsed, setCollapsed] = useState(true);
  useGlobalNotificationsWs();
  const chatUnreadTotal = useSelector(selectTotalUnread);
  const taskUnreadTotal = useSelector(selectUnreadTaskCount);
  const location = useLocation();

  const expandedByDefault = useMemo(() => {
    const open: Record<string, boolean> = {};
    for (const item of NAV_ITEMS) {
      if (item.kind === 'group') {
        open[item.id] = item.children.some((c) => location.pathname.startsWith(c.to));
      }
    }
    return open;
  }, [location.pathname]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(expandedByDefault);

  const toggleGroup = (id: string) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenGroups((s) => ({ ...s, [id]: true }));
      return;
    }
    setOpenGroups((s) => ({ ...s, [id]: !s[id] }));
  };

  const width = collapsed ? 72 : 240;

  return (
    <aside className="gt-sidebar" style={{ width }}>
      <div
        className="gt-sidebar-header"
        style={{ justifyContent: collapsed ? 'center' : 'space-between' }}
      >
        {!collapsed && <span className="gt-sidebar-brand">Teamlio</span>}
        <button
          type="button"
          className="gt-sidebar-collapse"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronsRight className="w-4 h-4" />
          ) : (
            <ChevronsLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '0 8px 8px 8px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
          marginBottom: 8,
        }}
      >
        <NotificationBell
          icon={<MessageSquare className="w-4 h-4" />}
          count={chatUnreadTotal}
          ariaLabel="Chat notifications"
          panelTitle="New chats"
        >
          {(close) => <ChatNotificationsPanel onItemSelected={close} />}
        </NotificationBell>
        <NotificationBell
          icon={<Bell className="w-4 h-4" />}
          count={taskUnreadTotal}
          ariaLabel="Task notifications"
          panelTitle="Task notifications"
        >
          {(close) => <TaskNotificationsPanel onItemSelected={close} />}
        </NotificationBell>
      </div>

      <nav className="gt-sidebar-nav">
        {NAV_ITEMS.map((item) => {
          if (item.kind === 'leaf') {
            const { to, label, Icon } = item;
            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `gt-sidebar-item${isActive ? ' is-active' : ''}`
                }
                style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
                title={collapsed ? label : undefined}
              >
                <Icon className="gt-sidebar-icon" />
                {!collapsed && <span className="gt-sidebar-label">{label}</span>}
              </NavLink>
            );
          }

          const { id, label, Icon, children } = item;
          const isOpen = !!openGroups[id];
          const groupActive = children.some((c) => location.pathname.startsWith(c.to));

          return (
            <div key={id}>
              <button
                type="button"
                className={`gt-sidebar-item${groupActive ? ' is-active' : ''}`}
                onClick={() => toggleGroup(id)}
                aria-expanded={isOpen}
                style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
                title={collapsed ? label : undefined}
              >
                <Icon className="gt-sidebar-icon" />
                {!collapsed && <span className="gt-sidebar-label">{label}</span>}
                {!collapsed && (
                  <ChevronDown
                    className="gt-sidebar-chevron"
                    style={{
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                  />
                )}
              </button>

              {!collapsed && isOpen && (
                <div className="gt-sidebar-submenu">
                  {children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      className={({ isActive }) =>
                        `gt-sidebar-subitem${isActive ? ' is-active' : ''}`
                      }
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div style={{ borderTop: '1px solid var(--gt-divider)', padding: 12 }}>
        <ProfileTab collapsed={collapsed} />
      </div>
    </aside>
  );
}
